import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Pencil, Languages, Brain, ArrowRight, RotateCcw, ChevronRight } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const ICONS = { BookOpen, Pencil, Languages, Brain };

export default function Categories() {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Categorías de tests" path="/tests" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-30" />

      <div className="relative">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">Categorías</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[0.95]">
            Elige tu campo<br /><span className="text-slate-500">de entrenamiento.</span>
          </h1>
          <p className="mt-3 text-base text-slate-400 max-w-xl">Cada categoría se divide en temas y bloques. Después elegirás el modo.</p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="categories-grid">
            {/* Failed questions special card */}
            <Link
              to="/tests/failed"
              className="group relative overflow-hidden bg-ink-800 border border-gcgold/30 hover:border-gcgold rounded-3xl p-7 transition-all hover:-translate-y-1"
              data-testid="failed-questions-card"
            >
              <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-rose-500/15 blur-3xl group-hover:bg-rose-500/25 transition-colors" />
              <div className="absolute inset-0 bg-dot-grid opacity-30" />
              <div className="relative">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-12 w-12 rounded-xl bg-gcgold/10 border border-gcgold/30 flex items-center justify-center">
                    <RotateCcw className="h-5 w-5 text-gcgold" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-gcgold font-bold">Repaso</span>
                </div>
                <h2 className="font-display text-2xl font-extrabold text-white tracking-tight">Preguntas falladas</h2>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">Practica las preguntas en las que te has equivocado anteriormente. Tu mejor herramienta para reforzar.</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[10px] font-mono font-bold text-gcgold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                  Repasar errores <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>

            {cats.map((c) => {
              const Icon = ICONS[c.icon] || BookOpen;
              return (
                <Link
                  key={c.id}
                  to={`/tests/${c.id}`}
                  className="group relative overflow-hidden bg-ink-800 hover:bg-ink-700 border border-white/5 hover:border-gcgold/30 rounded-3xl p-7 transition-all hover:-translate-y-1"
                  data-testid={`category-link-${c.id}`}
                >
                  <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-gcgreen/10 blur-3xl group-hover:bg-gcgreen/20 transition-colors" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className="h-12 w-12 rounded-xl bg-white/5 border border-white/10 group-hover:border-gcgold/40 group-hover:bg-gcgold/10 flex items-center justify-center transition-all">
                        <Icon className="h-5 w-5 text-gcgold" strokeWidth={1.5} />
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 font-bold">{c.question_count} preg</span>
                    </div>
                    <h2 className="font-display text-2xl font-extrabold text-white tracking-tight">{c.name}</h2>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.description}</p>

                    {(c.topics || []).length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-1.5">
                        {c.topics.slice(0, 4).map((t) => (
                          <span key={t.id} className="text-[10px] px-2 py-1 rounded-md bg-white/5 border border-white/5 text-slate-400 font-mono">
                            {t.name}
                          </span>
                        ))}
                        {c.topics.length > 4 && (
                          <span className="text-[10px] px-2 py-1 rounded-md text-gcgold font-mono">+{c.topics.length - 4} más</span>
                        )}
                      </div>
                    )}

                    <div className="mt-6 inline-flex items-center gap-1.5 text-[10px] font-mono font-bold text-gcgold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                      Configurar test <ChevronRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
